import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { BehaviorSubject, combineLatest, map, Observable, of, take } from 'rxjs';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';
import { GalaxyInfiniteScrollTriggerModule } from '@vendasta/galaxy/infinite-scroll-trigger';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { FormsModule } from '@angular/forms';
import { HostService, IsCalendarConfiguredRequestInterface, MeetingType } from '@vendasta/meetings';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { RxState } from '@rx-angular/state';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { DurationPipe, IsSelectedPipe } from '../pipes';
import { switchMap } from 'rxjs/operators';
import { MatTooltip } from '@angular/material/tooltip';

@Component({
  selector: 'crm-fulfillment-projects-association-card',
  imports: [
    CommonModule,
    TranslateModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatCardModule,
    GalaxyBadgeModule,
    MatDialogContent,
    MatDialogTitle,
    GalaxyFormFieldModule,
    MatDialogActions,
    GalaxyEmptyStateModule,
    GalaxyInfiniteScrollTriggerModule,
    MatRadioGroup,
    MatRadioButton,
    FormsModule,
    GalaxyLoadingSpinnerModule,
    DurationPipe,
    IsSelectedPipe,
    MatTooltip,
  ],
  templateUrl: './meeting-event-list.component.html',
  styleUrls: ['./meeting-event-list.component.scss'],
  providers: [RxState],
})
export class MeetingEventComponent implements OnInit {
  selectedMeetingType: string;

  meetingTypes: MeetingType[] = [];
  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

  calendarId$: Observable<string[]> = of([]);
  eventTypeList$: Observable<{ [p: string]: MeetingType[] }> = of({});
  currentUserId$: Observable<string> = of('');
  namespace$: Observable<string> = of('');
  parentNamespace$: Observable<string> = of('');

  constructor(
    readonly ref: MatDialogRef<MeetingEventComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      contactId: string;
      currentUserId: Observable<string>;
      namespace: Observable<string>;
      parentNamespace: Observable<string>;
    },
    private snackbarService: SnackbarService,
    private readonly meetingService: HostService,
  ) {
    this.currentUserId$ = data.currentUserId;
    this.namespace$ = data.namespace;
    this.parentNamespace$ = data.parentNamespace;
  }

  ngOnInit(): void {
    this.getMeetingTypes();
  }

  sendEmail() {
    if (!this.selectedMeetingType) {
      return;
    }

    this.sendEmailToContact(this.data.contactId, this.selectedMeetingType).subscribe({
      next: () => {
        this.snackbarService.openSuccessSnack('COMMON.SNACKBAR_MESSAGE.MEETING_EMAIL_SUCCESS', { action: 'Close' });
      },
      error: () => {
        this.snackbarService.openErrorSnack('COMMON.SNACKBAR_MESSAGE.MEETING_EMAIL_FAILURE');
      },
    });
    this.ref.close();
  }

  close() {
    this.ref.close();
  }

  getMeetingTypes(): void {
    this.calendarId$ = combineLatest([this.namespace$, this.currentUserId$, this.parentNamespace$]).pipe(
      map(([namespace, currentUser, parentNamespace]) => {
        const request: IsCalendarConfiguredRequestInterface = {
          applicationContextProperties: {
            ...(parentNamespace
              ? { business_id: namespace, user_context: 'SMB' }
              : { partner_id: namespace, user_context: 'PARTNER' }),
          },
          userId: currentUser as string,
        };
        return request;
      }),
      switchMap((request) => this.meetingService.IsCalendarConfigured(request)),
      map((response) => {
        return response.calendarIds;
      }),
    );

    this.eventTypeList$ = this.calendarId$.pipe(
      switchMap((calendarIds) => {
        const request = {
          calendarIds: calendarIds,
          metadata: {},
        };
        return this.meetingService.getMeetingTypesForCalendars(request);
      }),
    );

    this.eventTypeList$.pipe(take(1)).subscribe((meetingTypeMap: { [p: string]: MeetingType[] }) => {
      const meetingTypes: MeetingType[] = Object.values(meetingTypeMap).flat();
      this.meetingTypes = meetingTypes;
      this.loading$$.next(false);

      if (meetingTypes.length > 0) {
        this.selectedMeetingType = meetingTypes[0].id;
      } else {
        this.ref.close();
        this.snackbarService.openErrorSnack('COMMON.SNACKBAR_MESSAGE.NO_MEETING_TYPES');
      }
    });
  }

  sendEmailToContact(contactId: string, meetingTypeId: string) {
    return this.currentUserId$.pipe(
      map((currentUser) => ({
        contactId: contactId,
        userId: currentUser,
        meetingTypeId: meetingTypeId,
      })),
      switchMap((request) => this.meetingService.sendMeetingRequestEmail(request)),
    );
  }

  getBadgeColor(calendarType: string): 'yellow' | 'blue' | 'green' {
    switch (calendarType) {
      case 'Personal event type':
        return 'green';
      case 'Team event type':
        return 'blue';
      default:
        return 'yellow';
    }
  }
  getEventType(calendarType: string): string {
    const trimmedType = calendarType.trim().toLowerCase();
    if (trimmedType === 'personal event type') {
      return 'Personal';
    } else if (trimmedType === 'team event type') {
      return 'Team';
    }
    return calendarType.trim();
  }

  getTruncatedTeamName(teamName: string): string {
    return teamName.length > 20 ? `${teamName.slice(0, 20)}...` : teamName;
  }
}
